import { queryStream } from '@/features/workspaces/stream';
import { DOCUMENT_TYPE, type DocumentTypeName } from '@/features/documents/constants';
import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { isAxiosError } from 'axios';
import { ARTIFACTS_QUERY_KEY } from '@/features/artifacts/queries';
import { entityDocumentsQuery, indexingStatusQuery } from '@/features/documents/queries';
import { getIndexingState } from '@/features/documents/utils';
import { invalidateScopeConsumers } from '@/features/subcontractor-bids/queries';
import type { ScopeAgentStatus } from './types';
import { isScopeAgentRunActive, scopeBusyBuild, scopeDescribing, scopeRunEnded } from './utils';

export const SCOPE_DESCRIPTION_QUERY_KEY = 'scopeDescription';
export const SCOPE_PACKAGE_PREVIEW_QUERY_KEY = 'scopePackagePreview';
export const SCOPE_LINES_QUERY_KEY = 'scopeLines';
export const SCOPE_CLIENT_CHANGES_QUERY_KEY = 'scopeClientChanges';
export const SCOPE_LINE_HISTORY_QUERY_KEY = 'scopeLineHistory';

export const scopeAgentStatusQuery = (resourceId: string, type: DocumentTypeName) => {
  const queryKey = ['scopeAgent', resourceId];
  const refreshConsumers = (client: QueryClient, next: ScopeAgentStatus) => {
    if (scopeRunEnded(client.getQueryData<ScopeAgentStatus>(queryKey), next)) {
      invalidateScopeConsumers(client, resourceId);
      client.invalidateQueries(scopeBreakdownQuery(resourceId), { cancelRefetch: false });
      client.invalidateQueries({ queryKey: [ARTIFACTS_QUERY_KEY] });
    }
  };
  return queryOptions({
    queryKey,
    queryFn: async ({ client }) => {
      try {
        const { data: status } = await api.get<ScopeAgentStatus>('v1/work-packages/scope-agent', {
          params: { tenderId: resourceId },
        });
        refreshConsumers(client, status);
        if (isScopeAgentRunActive(status))
          client.invalidateQueries(scopeBreakdownQuery(resourceId), { cancelRefetch: false });
        return status;
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === 404 &&
          error.response.data?.detail === 'Not Found'
        )
          return {
            evaluationId: resourceId,
            artifactId: null,
            run: null,
            knowledgeBases: [],
            hasTenderBill: false,
            available: false,
          } satisfies ScopeAgentStatus;
        throw error;
      }
    },
    refetchInterval: (query) => (isScopeAgentRunActive(query.state.data) ? 30_000 : false),
    meta: {
      stream: queryStream<ScopeAgentStatus, ScopeAgentStatus | { entity: string }>({
        topic: 'scope_breakdown',
        type: DOCUMENT_TYPE[type],
        resourceId,
        isActive: isScopeAgentRunActive,
        onStatusUpdate: (payload, client) => {
          if (!('evaluationId' in payload)) return client.invalidateQueries({ queryKey });
          refreshConsumers(client, payload);
          client.invalidateQueries(scopeBreakdownQuery(resourceId), { cancelRefetch: false });
          client.setQueryData(queryKey, payload);
        },
      }),
    },
  });
};

export function useScopeBreakdownPreparation(resourceId: string, type: DocumentTypeName) {
  const documents = useQuery(entityDocumentsQuery(resourceId, type));
  const hasDocuments = documents.isSuccess && documents.data.length > 0;
  const source = useQuery({
    ...indexingStatusQuery(type, resourceId),
    enabled: hasDocuments,
  });
  return documents.isSuccess && !hasDocuments
    ? 'missing'
    : documents.isError || source.isError
      ? 'failed'
      : source.isPending
        ? 'loading'
        : hasDocuments && getIndexingState(source.data).isIndexed
          ? source.data?.indexedPercentage === 0
            ? 'unavailable'
            : 'ready'
          : 'preparing';
}

export const scopeBreakdownQuery = (resourceId: string) =>
  queryOptions({
    queryKey: ['scopeBreakdown', resourceId],
    queryFn: async () => {
      try {
        const { data } = await api.get<import('./types').ScopeBreakdown | null>(
          'v1/work-packages/scope-breakdown',
          { params: { tenderId: resourceId } },
        );
        return data;
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === 404 &&
          error.response.data?.detail === 'Not Found'
        )
          return null;
        throw error;
      }
    },
    refetchInterval: (query) =>
      query.state.data?.packages.some((pack) => scopeDescribing(pack.descriptionState))
        ? 15000
        : false,
  });

export const scopeCitationsQuery = (resourceId: string, revision: number, citationIds: string[]) =>
  queryOptions({
    queryKey: ['scopeCitations', resourceId, revision, citationIds],
    queryFn: async () => {
      const { data } = await api.get<import('./types').ScopePreviewCitation[]>(
        'v1/work-packages/scope-breakdown/citations',
        { params: { tenderId: resourceId, citationIds } },
      );
      return data;
    },
    staleTime: Infinity,
  });

export const scopeStandardSetupQuery = (resourceId: string) =>
  queryOptions({
    queryKey: ['scopeStandardSetup', resourceId],
    queryFn: async () => {
      const { data } = await api.get<import('./types').ScopeStandardSetup>(
        'v1/work-packages/scope-agent/setup',
        { params: { tenderId: resourceId } },
      );
      return data;
    },
    refetchInterval: ({ state: { data } }) =>
      data && [...data.files, ...data.referenceFiles].some((file) => file.status === 'processing')
        ? 5000
        : false,
  });

export const scopeStartModeQuery = (
  resourceId: string,
  files: import('./types').ScopeStandardSetup['files'],
) =>
  queryOptions({
    queryKey: ['scopeStartMode', resourceId, files.map(({ id, pages }) => [id, pages]).sort()],
    queryFn: async () =>
      (
        await api.post<{
          detection: {
            startMode: import('./types').ScopeStartMode;
            reason: string;
            contractForm: string | null;
            chapterCount: number | null;
            pricedLineCount: number | null;
          };
        }>('v1/work-packages/scope-agent/start-mode', null, { params: { tenderId: resourceId } })
      ).data.detection,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const scopeDescriptionQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: [SCOPE_DESCRIPTION_QUERY_KEY, resourceId, packageId],
    queryFn: async ({ client }) => {
      const previous = client.getQueryData<import('./types').ScopeDescription>(
        scopeDescriptionQuery(resourceId, packageId).queryKey,
      );
      const { data } = await api.get<import('./types').ScopeDescription>(
        `v1/work-packages/scope-breakdown/packages/${packageId}/description`,
        { params: { tenderId: resourceId } },
      );
      if (scopeDescribing(previous?.state) && !scopeDescribing(data.state))
        client.invalidateQueries(scopeBreakdownQuery(resourceId));
      return data;
    },
    refetchInterval: (query) => (scopeDescribing(query.state.data?.state) ? 2000 : false),
  });

export const scopePackageConditionsQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['scopePackageConditions', resourceId, packageId],
    queryFn: async () =>
      (
        await api.get<
          {
            sourceId: string;
            text?: string;
            displayText?: string;
            included: boolean;
            granularity: import('./types').ScopeLevel;
            contractGranularity: import('./types').ScopeLevel;
          }[]
        >(`v1/work-packages/scope-breakdown/packages/${packageId}/conditions`, {
          params: { tenderId: resourceId },
        })
      ).data,
  });

export const scopePackageDocumentsQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['scopePackageDocuments', resourceId, packageId],
    queryFn: async () =>
      (
        await api.get<import('./types').ScopePackageDocument[]>(
          `v1/work-packages/scope-breakdown/packages/${packageId}/documents`,
          { params: { tenderId: resourceId } },
        )
      ).data,
  });

export const scopeProofreadQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['scopeProofread', resourceId, packageId],
    queryFn: async () =>
      (
        await api.get<import('./types').ScopeProofreadCheck[]>(
          `v1/work-packages/scope-breakdown/packages/${packageId}/proofread`,
          { params: { tenderId: resourceId } },
        )
      ).data,
  });

export const scopeLinesQuery = (resourceId: string) =>
  queryOptions({
    queryKey: [SCOPE_LINES_QUERY_KEY, resourceId],
    queryFn: async () =>
      (
        await api.get<import('./types').ScopeLine[]>('v1/work-packages/scope-breakdown/lines', {
          params: { tenderId: resourceId },
        })
      ).data,
  });

export const scopeClientChangesQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: [SCOPE_CLIENT_CHANGES_QUERY_KEY, resourceId, packageId],
    queryFn: async () =>
      (
        await api.get<import('./types').ScopeClientChange[]>(
          `v1/work-packages/scope-breakdown/packages/${packageId}/changes`,
          { params: { tenderId: resourceId } },
        )
      ).data,
  });

export const scopeSectionGenerationsQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['scopeSectionGenerations', resourceId, packageId],
    queryFn: async ({ client, signal }) => {
      const { data } = await api.get<import('./types').ScopeSectionGeneration[]>(
        `v1/work-packages/scope-breakdown/packages/${packageId}/generation/sections`,
        {
          params: { tenderId: resourceId },
          signal,
        },
      );
      const previous = client.getQueryData<typeof data>(
        scopeSectionGenerationsQuery(resourceId, packageId).queryKey,
      );
      if (
        data.some(
          (section) =>
            section.state === 'succeeded' &&
            !previous?.some(
              (old) => old.operationId === section.operationId && old.state === 'succeeded',
            ),
        )
      )
        client.invalidateQueries(scopePackageDocumentsQuery(resourceId, packageId));
      return data;
    },
    refetchInterval: (query) =>
      query.state.data?.some((section) => scopeBusyBuild(section.state)) ? 3000 : false,
  });

export const scopePackageGenerationQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['scopePackageGeneration', resourceId, packageId],
    refetchIntervalInBackground: true,
    queryFn: async ({ client, signal }) => {
      const { data } = await api.get<import('./types').ScopePackageGeneration | null>(
        `v1/work-packages/scope-breakdown/packages/${packageId}/generation`,
        { params: { tenderId: resourceId }, signal },
      );
      const previous = client.getQueryData<import('./types').ScopePackageGeneration>(
        scopePackageGenerationQuery(resourceId, packageId).queryKey,
      );
      if (
        data?.state === 'succeeded' &&
        (previous?.state !== 'succeeded' ||
          previous.operationId !== data.operationId ||
          previous.snapshot?.id !== data.snapshot?.id)
      ) {
        client.invalidateQueries(scopePackageDocumentsQuery(resourceId, packageId));
        client.invalidateQueries(scopeDescriptionQuery(resourceId, packageId));
        client.invalidateQueries(scopeBreakdownQuery(resourceId));
      }
      return data;
    },
    refetchInterval: (query) => (scopeBusyBuild(query.state.data?.state) ? 3000 : false),
  });

export const scopePackagePreviewPdfQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: ['scopePackagePreviewPdf', resourceId, packageId],
    queryFn: async () =>
      (
        await api.get<Blob>(`v1/work-packages/scope-breakdown/packages/${packageId}/preview.pdf`, {
          params: { tenderId: resourceId },
          responseType: 'blob',
        })
      ).data,
    staleTime: 0,
    gcTime: 0,
  });

export const scopePackagePreviewQuery = (resourceId: string, packageId: string) =>
  queryOptions({
    queryKey: [SCOPE_PACKAGE_PREVIEW_QUERY_KEY, resourceId, packageId],
    queryFn: async () =>
      (
        await api.get<import('./types').ScopePackagePreview>(
          `v1/work-packages/scope-breakdown/packages/${packageId}/preview`,
          { params: { tenderId: resourceId } },
        )
      ).data,
    staleTime: 0,
  });

export const scopePackageFileQuery = (
  resourceId: string,
  packageId: string,
  version: number,
  fileId: string,
) =>
  queryOptions({
    queryKey: ['scopePackageFile', resourceId, packageId, version, fileId],
    queryFn: async () =>
      (
        await api.get<Blob>(
          `v1/work-packages/scope-breakdown/packages/${packageId}/versions/${version}/files/${fileId}`,
          { params: { tenderId: resourceId }, responseType: 'blob' },
        )
      ).data,
  });

export const scopeLineHistoryQuery = (resourceId: string, packageId: string, lineId: string) =>
  queryOptions({
    queryKey: [SCOPE_LINE_HISTORY_QUERY_KEY, resourceId, packageId, lineId],
    queryFn: async () =>
      (
        await api.get<
          {
            actor: string;
            at: string;
            before: import('./types').ScopeLine | null;
            after: import('./types').ScopeLine;
          }[]
        >(`v1/work-packages/scope-breakdown/packages/${packageId}/lines/${lineId}/history`, {
          params: { tenderId: resourceId },
        })
      ).data,
  });

export const invalidateScopeMembership = (client: QueryClient, resourceId: string) => {
  client.invalidateQueries({ queryKey: [SCOPE_PACKAGE_PREVIEW_QUERY_KEY, resourceId] });
  return Promise.all([
    client.invalidateQueries(scopeBreakdownQuery(resourceId)),
    client.invalidateQueries({ queryKey: [SCOPE_DESCRIPTION_QUERY_KEY, resourceId] }),
    client.invalidateQueries(scopeLinesQuery(resourceId)),
    invalidateScopeConsumers(client, resourceId),
  ]);
};
